import {useNavigate} from "react-router-dom"
import React, {useState} from "react"
import * as Yup from "yup"
import {useFormik} from "formik"
import notifyToast from "../../components/Toast/Toast"
import {ToastContainer} from "react-toastify"
import TitleComponent from "../../components/TitleComponent/TitleComponent"
import LoadingComponent from "../../components/LoadingComponent/LoadingComponent"
import clsx from "clsx"
import {PrijedloziZaPoboljsanja} from "../../models/UposlenikModel"
import {savePrijedlog} from "../../api/prijedloziZaPoboljsanja"

const PrijedloziZaPoboljsanjeDetails: React.FC = () => {
    const [loading, setLoading] = useState(false)

    const initialValues = {
        Naslov: "",
        Opis: "",
    }

    const [initialData, setInitialData] = useState<Partial<PrijedloziZaPoboljsanja>>(initialValues)

    const validationSchema = Yup.object({
        Naslov: Yup.string().min(2, "Mora imati barem 2 karaktera").required("Obavezno polje"),
        Opis: Yup.string().min(10, "Mora imati barem 10 karaktera").required("Obavezno polje"),
    })

    const navigate = useNavigate()

    const formik = useFormik({
        initialValues: initialData,
        validationSchema: validationSchema,
        onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
            setLoading(true)
            try {
                const response = await savePrijedlog(values)
                if (response.error === 0) {
                    notifyToast(true, response.message)
                    resetForm()
                    setTimeout(() => {
                        navigate(-1)
                    }, 3000)
                } else {
                    notifyToast(false, response.message || "An unexpected error occurred.")
                }
            } catch (error) {
                console.error("Error: ", error)
                notifyToast(false, "Error")
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    const titleLen = (formik.values.Naslov || "").trim().length
    const descLen = (formik.values.Opis || "").trim().length

    return (
        <div className="card mb-5 mb-xl-10">
            <ToastContainer />

            <div
                className="card-header border-0 cursor-pointer"
                role="button"
                data-bs-target="#kt_account_profile_details"
                aria-expanded="true"
                aria-controls="kt_account_profile_details"
            >
                <div className="card-title m-0 d-flex flex-column">
                    <TitleComponent name="Prijedlog za poboljšanje" />
                    <div className="text-muted fs-7 mt-1">
                        Napišite prijedlog koji može unaprijediti posao, sigurnost ili uštedjeti vrijeme.
                    </div>
                </div>
            </div>

            <div id="kt_account_profile_details" className="collapse show">
                {loading ? (
                    <div className="p-9">
                        <LoadingComponent />
                    </div>
                ) : (
                    <form onSubmit={formik.handleSubmit} noValidate className="form">
                        <div className="card-body border-top p-9">
                            <div className="row g-6">
                                {/* LEFT: INPUTS */}
                                <div className="col-12 col-lg-6 order-2 order-lg-1">
                                    {/* NASLOV */}
                                    <div className="mb-6">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <label className="required fw-bold fs-6 mb-2">1) Naslov prijedloga</label>
                                            <span className={`badge ${titleLen >= 2 ? "bg-success" : "bg-light text-muted border"}`}>
            {titleLen}/2+
          </span>
                                        </div>

                                        <input
                                            type="text"
                                            placeholder="Npr. Bolje označavanje paleta"
                                            {...formik.getFieldProps("Naslov")}
                                            className={clsx(
                                                "form-control form-control-lg form-control-solid",
                                                {"is-invalid": formik.touched.Naslov && formik.errors.Naslov},
                                                {"is-valid": formik.touched.Naslov && !formik.errors.Naslov}
                                            )}
                                        />

                                        <div className="form-text">
                                            Kratko i jasno: šta je prijedlog u jednoj rečenici.
                                        </div>

                                        {formik.touched.Naslov && formik.errors.Naslov && (
                                            <div className="fv-plugins-message-container mt-2">
                                                <div className="fv-help-block text-danger">{formik.errors.Naslov}</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* OPIS */}
                                    <div className="mb-6">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <label className="required fw-bold fs-6 mb-2">2) Opis prijedloga</label>
                                            <span className={`badge ${descLen >= 10 ? "bg-success" : "bg-light text-muted border"}`}>
            {descLen}/10+
          </span>
                                        </div>

                                        <textarea
                                            rows={8}
                                            placeholder={`Primjer:
Problem: ...
Prijedlog: ...
Korist: ...`}
                                            {...formik.getFieldProps("Opis")}
                                            className={clsx(
                                                "form-control form-control-lg form-control-solid",
                                                {"is-invalid": formik.touched.Opis && formik.errors.Opis},
                                                {"is-valid": formik.touched.Opis && !formik.errors.Opis}
                                            )}
                                        />

                                        <div className="form-text">
                                            Napišite: <b>Problem</b> → <b>Rješenje</b> → <b>Korist</b>.
                                        </div>

                                        {formik.touched.Opis && formik.errors.Opis && (
                                            <div className="fv-plugins-message-container mt-2">
                                                <div className="fv-help-block text-danger">{formik.errors.Opis}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* RIGHT: EXPLANATION */}
                                <div className="col-12 col-lg-6 order-1 order-lg-2">
                                    {/* Optional: make it “sticky” on desktop so it stays visible while typing */}
                                    <div className="position-sticky" style={{top: "90px"}}>
                                        {/* WHY IT MATTERS */}
                                        <div className="alert alert-info mb-4">
                                            <div className="fw-bold mb-1">Zašto je važno poslati prijedlog?</div>
                                            <div className="text-gray-800">
                                                Dobar prijedlog pomaže da se:
                                                <ul className="mb-0 mt-2 ps-4">
                                                    <li>brže riješe svakodnevni problemi</li>
                                                    <li>poveća sigurnost i kvalitet</li>
                                                    <li>uštedi vrijeme i materijal</li>
                                                    <li>olakša posao svima</li>
                                                </ul>
                                            </div>
                                            <div className="text-muted fs-7 mt-2">
                                                Najkorisnije je: <b>problem</b> + <b>rješenje</b> + <b>korist</b>.
                                            </div>
                                        </div>

                                        {/* HOW TO (highlighted) */}
                                        <div
                                            className="mb-4 p-4 rounded"
                                            style={{
                                                backgroundColor: "#fff8e1",
                                                borderLeft: "6px solid #ffc107",
                                            }}
                                        >
                                            <div className="d-flex align-items-center mb-2">
                                                <span className="me-2 fs-3">✍️</span>
                                                <div className="fw-bold fs-5 text-dark">Kako napisati dobar prijedlog</div>
                                            </div>

                                            <ol className="mb-0 ps-4 text-dark">
                                                <li className="mb-1">
                                                    <b>Naslov:</b> 3–8 riječi{" "}
                                                    <span className="text-muted">(npr. “Bolje označavanje paleta”)</span>
                                                </li>
                                                <li className="mb-1">
                                                    <b>Opis:</b> napiši <u>šta je problem</u> + <u>kako da bude bolje</u>
                                                </li>
                                                <li>
                                                    Ako može: dodaj primjer (gdje se dešava, koliko često).
                                                </li>
                                            </ol>
                                        </div>

                                        {/* EXAMPLE */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-body">
                                                <div className="fw-bold mb-2">Primjer dobrog prijedloga</div>
                                                <div className="text-muted fs-7">
                                                    <b>Naslov:</b> Bolje označavanje paleta<br />
                                                    <b>Problem:</b> Palete se često pomiješaju kod utovara.<br />
                                                    <b>Rješenje:</b> Uvesti 2 boje etiketa + stalno mjesto za odlaganje.<br />
                                                    <b>Korist:</b> Manje grešaka i brže pakovanje.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </form>
                )}
            </div>
        </div>
    )
}

export default PrijedloziZaPoboljsanjeDetails
